import { UNIT_CSV_TEMPLATE } from '@sitewise/shared';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@sitewise/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  commitUnitImport,
  previewUnitImport,
  type UnitImportPreview,
} from '../../../api/buildings.js';
import { ApiError } from '../../../lib/api-client.js';

type DuplicatePolicy = 'override' | 'ignore';

export function ImportUnitsDialog({
  open,
  onOpenChange,
  buildingId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildingId: string;
}) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [csv, setCsv] = useState<string | null>(null);
  const [preview, setPreview] = useState<UnitImportPreview | null>(null);
  const [policy, setPolicy] = useState<DuplicatePolicy | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setFileName(null);
    setCsv(null);
    setPreview(null);
    setPolicy(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  function close() {
    reset();
    onOpenChange(false);
  }

  const previewMutation = useMutation({
    mutationFn: (text: string) => previewUnitImport(buildingId, text),
    onSuccess: (result) => {
      setPreview(result);
      setPolicy(null);
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not read that file'),
  });

  const commitMutation = useMutation({
    mutationFn: (choice: DuplicatePolicy) => commitUnitImport(buildingId, csv!, choice),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['building', buildingId, 'units'] });
      void queryClient.invalidateQueries({ queryKey: ['building', buildingId] });
      const parts = [`${result.inserted} added`];
      if (result.updated > 0) parts.push(`${result.updated} updated`);
      if (result.ignored > 0) parts.push(`${result.ignored} ignored`);
      if (result.unchanged > 0) parts.push(`${result.unchanged} unchanged`);
      toast.success(parts.join(', ') + '.');
      close();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not import units'),
  });

  async function onFile(file: File) {
    setError(null);
    setPreview(null);
    setPolicy(null);
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setFileName(null);
      setCsv(null);
      setError('Use a .csv file. In Excel, choose Save As and pick CSV.');
      return;
    }
    const text = await file.text();
    setFileName(file.name);
    setCsv(text);
    previewMutation.mutate(text);
  }

  function downloadTemplate() {
    const blob = new Blob([UNIT_CSV_TEMPLATE], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'units-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  const existing = preview?.summary.existing ?? 0;
  const invalid = preview?.summary.invalid ?? 0;
  const canCommit =
    Boolean(csv) &&
    Boolean(preview) &&
    invalid === 0 &&
    (preview?.summary.total ?? 0) > 0 &&
    (existing === 0 || policy !== null) &&
    !commitMutation.isPending;

  const visibleRows = preview?.rows.slice(0, 12) ?? [];
  const hiddenCount = (preview?.rows.length ?? 0) - visibleRows.length;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import units</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-fg-secondary">
            Required column: <span className="font-medium text-fg">unit_number</span>. Optional:
            floor, type, occupancy, bedrooms, bathrooms, square_feet, notes, gas, and electric.
            Blank cells are left as they are when you override an existing unit.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="h-4 w-4" /> Template
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-4 w-4" /> {fileName ?? 'Choose CSV'}
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void onFile(file);
              }}
            />
          </div>

          {previewMutation.isPending ? (
            <p className="text-sm text-fg-muted">Reading file…</p>
          ) : null}

          {preview ? (
            <div className="space-y-3">
              <p className="text-sm">
                {preview.summary.new} new
                {preview.summary.existing > 0 ? `, ${preview.summary.existing} already exist` : ''}
                {preview.summary.invalid > 0 ? `, ${preview.summary.invalid} invalid` : ''}
              </p>
              <ul className="max-h-48 space-y-1 overflow-auto rounded border border-border p-2 text-sm">
                {visibleRows.map((row) => (
                  <li key={`${row.rowNumber}-${row.unitNumber ?? 'blank'}`} className="flex items-start gap-2">
                    <span className="w-8 shrink-0 tabular-nums text-fg-muted">{row.rowNumber}</span>
                    <span className="min-w-0 flex-1">
                      <span className="font-medium">{row.unitNumber ?? '—'}</span>
                      {row.fields.length > 0 ? (
                        <span className="text-fg-muted"> · {row.fields.join(', ')}</span>
                      ) : null}
                      {row.message ? <span className="block text-xs text-fg-secondary">{row.message}</span> : null}
                    </span>
                    <OutcomeBadge outcome={row.outcome} />
                  </li>
                ))}
              </ul>
              {hiddenCount > 0 ? (
                <p className="text-xs text-fg-muted">And {hiddenCount} more rows.</p>
              ) : null}

              {existing > 0 && invalid === 0 ? (
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium">
                    {existing} unit{existing === 1 ? '' : 's'} already exist. What should happen to them?
                  </legend>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="unit-import-policy"
                      className="mt-1"
                      checked={policy === 'override'}
                      onChange={() => setPolicy('override')}
                    />
                    <span>
                      <span className="font-medium">Override. </span>
                      Update those units with the values in this file. Work orders and documents stay.
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="unit-import-policy"
                      className="mt-1"
                      checked={policy === 'ignore'}
                      onChange={() => setPolicy('ignore')}
                    />
                    <span>
                      <span className="font-medium">Ignore. </span>
                      Skip the existing units and add only the new ones.
                    </span>
                  </label>
                </fieldset>
              ) : null}
            </div>
          ) : null}

          {error ? <p className="text-sm text-danger">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canCommit}
            onClick={() => commitMutation.mutate(existing > 0 ? policy! : 'ignore')}
          >
            {commitMutation.isPending ? 'Importing…' : 'Import units'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OutcomeBadge({ outcome }: { outcome: 'new' | 'existing' | 'invalid' }) {
  if (outcome === 'new') return <Badge variant="success">New</Badge>;
  if (outcome === 'existing') return <Badge variant="warning">Exists</Badge>;
  return <Badge variant="danger">Invalid</Badge>;
}
